import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const Card = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.xxl};
  width: 100%;
  max-width: 500px;
  box-shadow: ${props => props.theme.shadows.xl};
  text-align: center;
`;

const Icon = styled.div`
  width: 80px;
  height: 80px;
  background: ${props => props.theme.colors.accent}20;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing.lg};
  font-size: 2rem;
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.h2.fontSize};
  font-weight: ${props => props.theme.typography.h2.fontWeight};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Message = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.body.fontSize};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Email = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-weight: 500;
  color: ${props => props.theme.colors.accent};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ResendButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-size: ${props => props.theme.typography.body.fontSize};
  font-weight: 500;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accentHover};
  }
`;

const BackButton = styled(Link)`
  background: transparent;
  color: ${props => props.theme.colors.text.secondary};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  text-decoration: none;
  display: inline-block;
  text-align: center;
  font-size: ${props => props.theme.typography.body.fontSize};
  
  &:hover {
    background: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const EmailVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resendVerificationEmail } = useAuth();
  const [isResending, setIsResending] = useState(false);
  
  const email = location.state?.email;
  const message = location.state?.message || 'Please check your email to verify your account.';

  // If no email in state, redirect to register
  if (!email) {
    navigate('/register');
    return null;
  }

  const handleResendEmail = async () => {
    try {
      setIsResending(true);
      await resendVerificationEmail(email);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.message || 'Failed to resend email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Container>
      <Card
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Icon>📧</Icon>
        <Title>Verify Your Email</Title>
        <Message>{message}</Message>
        <Email>{email}</Email>
        
        <ButtonGroup>
          <ResendButton 
            onClick={handleResendEmail}
            disabled={isResending}
          >
            {isResending ? 'Sending...' : 'Resend Verification Email'}
          </ResendButton>
          <BackButton to="/login">
            Back to Sign In
          </BackButton>
        </ButtonGroup>
      </Card>
    </Container>
  );
};

export default EmailVerification; 